import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSearch, FaUsers, FaTree } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const ManageCasteGotra = () => {
    const [activeTab, setActiveTab] = useState('caste'); // 'caste' or 'gotra'
    const [casteList, setCasteList] = useState([]);
    const [gotraList, setGotraList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination
    const [castePage, setCastePage] = useState(1);
    const [gotraPage, setGotraPage] = useState(1);
    const [casteTotal, setCasteTotal] = useState(0);
    const [gotraTotal, setGotraTotal] = useState(0);
    const itemsPerPage = 20;

    useEffect(() => {
        if (activeTab === 'caste') {
            fetchCastes();
        } else {
            fetchGotras();
        }
    }, [activeTab, castePage, gotraPage, searchTerm]);

    // Fetch Castes
    const fetchCastes = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`https://app.bihebari.com.np/api/admin/caste-gotra/castes?page=${castePage}&limit=${itemsPerPage}&search=${searchTerm}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCasteList(response.data.data);
                setCasteTotal(response.data.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching castes:', error);
            toast.error('Error loading castes');
        } finally {
            setLoading(false);
        }
    };

    // Fetch Gotras
    const fetchGotras = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`https://app.bihebari.com.np/api/admin/caste-gotra/gotras?page=${gotraPage}&limit=${itemsPerPage}&search=${searchTerm}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setGotraList(response.data.data);
                setGotraTotal(response.data.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching gotras:', error);
            toast.error('Error loading gotras');
        } finally {
            setLoading(false);
        }
    };

    // Add/Update Caste
    const handleSaveCaste = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error('Please enter caste name');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            if (editingItem) {
                // Update
                await axios.put(`https://app.bihebari.com.np/api/admin/caste-gotra/castes/${editingItem.id}`, 
                    { caste_name: formData.name },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Caste updated successfully');
            } else {
                // Create
                await axios.post('https://app.bihebari.com.np/api/admin/caste-gotra/castes', 
                    { caste_name: formData.name },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Caste added successfully');
            }
            setShowModal(false);
            setFormData({ name: '' });
            setEditingItem(null);
            fetchCastes();
        } catch (error) {
            console.error('Error saving caste:', error);
            toast.error(error.response?.data?.message || 'Error saving caste');
        } finally {
            setLoading(false);
        }
    };

    // Add/Update Gotra
    const handleSaveGotra = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error('Please enter gotra name');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            if (editingItem) {
                // Update
                await axios.put(`https://app.bihebari.com.np/api/admin/caste-gotra/gotras/${editingItem.id}`, 
                    { gotra_name: formData.name },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Gotra updated successfully');
            } else {
                // Create
                await axios.post('https://app.bihebari.com.np/api/admin/caste-gotra/gotras', 
                    { gotra_name: formData.name },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Gotra added successfully');
            }
            setShowModal(false);
            setFormData({ name: '' });
            setEditingItem(null);
            fetchGotras();
        } catch (error) {
            console.error('Error saving gotra:', error);
            toast.error(error.response?.data?.message || 'Error saving gotra');
        } finally {
            setLoading(false);
        }
    };

    // Delete Caste
    const handleDeleteCaste = async (id) => {
        if (window.confirm('Are you sure you want to delete this caste?')) {
            setLoading(true);
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`https://app.bihebari.com.np/api/admin/caste-gotra/castes/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Caste deleted successfully');
                fetchCastes();
            } catch (error) {
                console.error('Error deleting caste:', error);
                toast.error(error.response?.data?.message || 'Error deleting caste');
            } finally {
                setLoading(false);
            }
        }
    };

    // Delete Gotra
    const handleDeleteGotra = async (id) => {
        if (window.confirm('Are you sure you want to delete this gotra?')) {
            setLoading(true);
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`https://app.bihebari.com.np/api/admin/caste-gotra/gotras/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Gotra deleted successfully');
                fetchGotras();
            } catch (error) {
                console.error('Error deleting gotra:', error);
                toast.error(error.response?.data?.message || 'Error deleting gotra');
            } finally {
                setLoading(false);
            }
        }
    };

    const openAddModal = () => {
        setEditingItem(null);
        setFormData({ name: '' });
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({ name: activeTab === 'caste' ? item.caste_name : item.gotra_name });
        setShowModal(true);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCastePage(1);
        setGotraPage(1);
    };

    const handlePageChange = (newPage) => {
        if (activeTab === 'caste') {
            setCastePage(newPage);
        } else {
            setGotraPage(newPage);
        }
    };

    const totalPages = activeTab === 'caste' ? Math.ceil(casteTotal / itemsPerPage) : Math.ceil(gotraTotal / itemsPerPage);
    const currentPage = activeTab === 'caste' ? castePage : gotraPage;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-blue-600 rounded-xl p-6 mb-8 text-white">
                    <h1 className="text-2xl font-bold">Manage Castes & Gotras</h1>
                    <p className="text-white/80 mt-1">Add, edit, or delete caste and gotra options for registration forms</p>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="flex border-b">
                        <button
                            className={`flex-1 px-6 py-4 text-center font-semibold transition-all duration-300 ${
                                activeTab === 'caste'
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setActiveTab('caste')}
                        >
                            <FaUsers className="inline mr-2" />
                            Manage Castes
                        </button>
                        <button
                            className={`flex-1 px-6 py-4 text-center font-semibold transition-all duration-300 ${
                                activeTab === 'gotra'
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setActiveTab('gotra')}
                        >
                            <FaTree className="inline mr-2" />
                            Manage Gotras
                        </button>
                    </div>
                </div>

                {/* Search and Add Button */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex-1 relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                            />
                        </div>
                        <button
                            onClick={openAddModal}
                            className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
                        >
                            <FaPlus />
                            <span>Add New {activeTab === 'caste' ? 'Caste' : 'Gotra'}</span>
                        </button>
                    </div>
                </div>

                {/* List Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {activeTab === 'caste' ? 'Caste Name' : 'Gotra Name'}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center">
                                            <div className="flex justify-center">
                                                <div className="w-8 h-8 border-t-2 border-red-600 rounded-full animate-spin"></div>
                                            </div>
                                            <p className="mt-2 text-gray-500">Loading...</p>
                                        </td>
                                    </tr>
                                )}
                                
                                {!loading && activeTab === 'caste' && casteList.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                            No castes found. Click "Add New Caste" to create one.
                                        </td>
                                    </tr>
                                )}
                                
                                {!loading && activeTab === 'gotra' && gotraList.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                            No gotras found. Click "Add New Gotra" to create one.
                                        </td>
                                    </tr>
                                )}
                                
                                {!loading && activeTab === 'caste' && casteList.map((caste) => (
                                    <tr key={caste.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{caste.id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{caste.caste_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(caste.created).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                onClick={() => openEditModal(caste)}
                                                className="text-blue-600 hover:text-blue-800 mr-3"
                                            >
                                                <FaEdit className="inline" /> Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCaste(caste.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <FaTrash className="inline" /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                
                                {!loading && activeTab === 'gotra' && gotraList.map((gotra) => (
                                    <tr key={gotra.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{gotra.id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{gotra.gotra_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(gotra.created).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                onClick={() => openEditModal(gotra)}
                                                className="text-blue-600 hover:text-blue-800 mr-3"
                                            >
                                                <FaEdit className="inline" /> Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteGotra(gotra.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <FaTrash className="inline" /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingItem ? `Edit ${activeTab === 'caste' ? 'Caste' : 'Gotra'}` : `Add New ${activeTab === 'caste' ? 'Caste' : 'Gotra'}`}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={activeTab === 'caste' ? handleSaveCaste : handleSaveGotra} className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {activeTab === 'caste' ? 'Caste Name' : 'Gotra Name'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                                    placeholder={`Enter ${activeTab === 'caste' ? 'caste' : 'gotra'} name`}
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                                    ) : (
                                        <FaSave />
                                    )}
                                    <span>{editingItem ? 'Update' : 'Save'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageCasteGotra;